import React, {
    useMemo,
    useState
} from "react"
import {
    Box,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    MenuItem,
    Chip,
    Button,
    Avatar,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TableFooter,
    Skeleton,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery
} from "@mui/material"
import {
    SearchOutlined,
    RefreshOutlined,
    ApartmentOutlined,
    PeopleAltOutlined,
    BadgeOutlined,
    SchoolOutlined,
    FileDownloadOutlined
} from "@mui/icons-material"
import _ from "lodash"
import { useGetBillingQuery } from "../Redux/actions/setting.action"

const STATUS_META = {
    active: { label: "Active", color: "#047857", bg: "#ecfdf5" },
    expiring: { label: "Expiring soon", color: "#b45309", bg: "#fffbeb" },
    expired: { label: "Expired", color: "#b91c1c", bg: "#fef2f2" },
    no_expiry: { label: "No expiry", color: "#475569", bg: "#f1f5f9" }
}

const SORTS = {
    most_users: {
        label: "Most users",
        compare: (a, b) => b.total_users - a.total_users || a.name.localeCompare(b.name)
    },
    least_users: {
        label: "Fewest users",
        compare: (a, b) => a.total_users - b.total_users || a.name.localeCompare(b.name)
    },
    name: {
        label: "Name A–Z",
        compare: (a, b) => a.name.localeCompare(b.name)
    }
}

const COUNT_COLUMNS = [
    { key: "admin_count", label: "Admins" },
    { key: "staff_count", label: "Staff" },
    { key: "student_count", label: "Students" }
]

/* =========================
   HELPERS
========================= */

const isImageUrl = (logo) =>
    typeof logo === "string" && /^(https?:|data:image\/)/.test(logo)

const initialsOf = (name = "") =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()

const formatCount = (value) => Number(value || 0).toLocaleString("en-IN")

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`

function downloadCsv(clients) {
    const header = ["Institution", "District", "State", "Status", "Admins", "Staff", "Students", "Total users"]
    const rows = clients.map((client) => [
        client.name,
        client.district,
        client.state,
        _.get(STATUS_META, [client.status, "label"], ""),
        client.admin_count,
        client.staff_count,
        client.student_count,
        client.total_users
    ])
    const csv = [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n")
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }))
    const link = document.createElement("a")
    link.href = url
    link.download = `billing_users_${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
}

/* =========================
   SMALL COMPONENTS
========================= */

function StatusChip({ status }) {
    const meta = STATUS_META[status] || STATUS_META.no_expiry
    return (
        <Chip
            size="small"
            label={meta.label}
            sx={{ color: meta.color, bgcolor: meta.bg, fontWeight: 600, fontSize: 12, borderRadius: "8px" }}
        />
    )
}

function ClientIdentity({ client }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Avatar
                src={isImageUrl(client.logo) ? client.logo : undefined}
                variant="rounded"
                sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    bgcolor: "var(--color-primary-light)",
                    color: "var(--color-primary)",
                    fontWeight: 700,
                    fontSize: 14
                }}
            >
                {initialsOf(client.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }} noWrap>
                    {client.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "var(--text-light)" }} noWrap>
                    {[client.district, client.state].filter(Boolean).join(", ")}
                </Typography>
            </Box>
        </Box>
    )
}

/* =========================
   MAIN
========================= */

export default function Billing() {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("md"))
    const { data, isLoading, isFetching, isError, error, refetch } = useGetBillingQuery()

    const [search, setSearch] = useState("")
    const [sort, setSort] = useState("most_users")

    const clients = useMemo(() => _.get(data, "list", []), [data])
    const totals = _.get(data, "totals", {})

    const visibleClients = useMemo(() => {
        const query = search.trim().toLowerCase()
        return clients
            .filter((client) => {
                if (!query) return true
                return [client.name, client.district, client.state]
                    .some((value) => value && String(value).toLowerCase().includes(query))
            })
            .sort(SORTS[sort].compare)
    }, [clients, search, sort])

    // Footer totals follow the search so they always match the visible rows
    const visibleTotals = useMemo(() => {
        const keys = [...COUNT_COLUMNS.map((column) => column.key), "total_users"]
        return Object.fromEntries(keys.map((key) => [key, _.sumBy(visibleClients, key)]))
    }, [visibleClients])

    /* ---------- error ---------- */

    if (isError) {
        const detail = _.get(error, "data.detail")
        return (
            <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "16px", border: "1px solid var(--border-color)" }}>
                <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
                    Couldn't load billing
                </Typography>
                <Typography sx={{ color: "var(--text-secondary)", fontSize: 14, mb: 3 }}>
                    {_.isString(detail) ? detail : "Check that the server is running, then try again."}
                </Typography>
                <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={refetch} sx={{ textTransform: "none" }}>
                    Try again
                </Button>
            </Paper>
        )
    }

    const summaryCards = [
        { key: "clients", label: "Institutions", value: clients.length, icon: <ApartmentOutlined />, color: "var(--color-primary)" },
        { key: "total_users", label: "Total users", value: totals.total_users, icon: <PeopleAltOutlined />, color: "#047857" },
        { key: "staff", label: "Admins & staff", value: (totals.admin_count || 0) + (totals.staff_count || 0), icon: <BadgeOutlined />, color: "#b45309" },
        { key: "student_count", label: "Students", value: totals.student_count, icon: <SchoolOutlined />, color: "#7c3aed" }
    ]

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* ================= SUMMARY ================= */}

            <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                gap: 2
            }}>
                {summaryCards.map((card) => (
                    <Paper
                        key={card.key}
                        elevation={0}
                        sx={{ p: 2, borderRadius: "14px", border: "1px solid var(--border-color)", bgcolor: "var(--bg-card)" }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "var(--text-secondary)" }}>
                            {React.cloneElement(card.icon, { sx: { fontSize: 18, color: card.color } })}
                            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                {card.label}
                            </Typography>
                        </Box>
                        {isLoading ? (
                            <Skeleton width={60} height={36} />
                        ) : (
                            <Typography sx={{ fontSize: 28, fontWeight: 700, color: card.color, fontFamily: "var(--font-heading)" }}>
                                {formatCount(card.value)}
                            </Typography>
                        )}
                    </Paper>
                ))}
            </Box>

            {/* ================= LIST ================= */}

            <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid var(--border-color)", overflow: "hidden" }}>

                {/* Toolbar */}
                <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    gap: 1.5,
                    p: 2,
                    borderBottom: "1px solid var(--border-color)"
                }}>
                    <TextField
                        size="small"
                        placeholder="Search by institution or location"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#f8fafc" } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <TextField
                            select
                            size="small"
                            label="Sort by"
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            sx={{ minWidth: 160, flex: { xs: 1, md: "none" }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        >
                            {Object.entries(SORTS).map(([key, option]) => (
                                <MenuItem key={key} value={key}>{option.label}</MenuItem>
                            ))}
                        </TextField>
                        <Tooltip title="Export CSV">
                            <span>
                                <IconButton
                                    onClick={() => downloadCsv(visibleClients)}
                                    disabled={visibleClients.length === 0}
                                    sx={{ border: "1px solid var(--border-color)", borderRadius: "10px" }}
                                    aria-label="Export billing as CSV"
                                >
                                    <FileDownloadOutlined fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Refresh">
                            <span>
                                <IconButton
                                    onClick={refetch}
                                    disabled={isFetching}
                                    sx={{ border: "1px solid var(--border-color)", borderRadius: "10px" }}
                                    aria-label="Refresh billing"
                                >
                                    <RefreshOutlined fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Body */}
                {isLoading ? (
                    <Box sx={{ p: 2 }}>
                        {[0, 1, 2, 3].map((row) => (
                            <Skeleton key={row} height={64} sx={{ borderRadius: "10px" }} />
                        ))}
                    </Box>
                ) : visibleClients.length === 0 ? (
                    <Box sx={{ py: 7, px: 3, textAlign: "center" }}>
                        <ApartmentOutlined sx={{ fontSize: 40, color: "var(--text-light)", mb: 1 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                            {clients.length === 0 ? "No institutions yet" : "No institutions match"}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "var(--text-secondary)" }}>
                            {clients.length === 0
                                ? "Onboarded schools and colleges will appear here."
                                : "Try a different search."}
                        </Typography>
                    </Box>
                ) : isMobile ? (

                    /* Mobile cards */
                    <Box>
                        {visibleClients.map((client) => (
                            <Box key={client.id} sx={{ p: 2, borderBottom: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 1.5 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                    <ClientIdentity client={client} />
                                    <StatusChip status={client.status} />
                                </Box>
                                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
                                    {[...COUNT_COLUMNS, { key: "total_users", label: "Total" }].map((column) => (
                                        <Box key={column.key}>
                                            <Typography sx={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase" }}>
                                                {column.label}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: 15,
                                                fontWeight: column.key === "total_users" ? 700 : 500,
                                                color: column.key === "total_users" ? "var(--color-primary)" : "var(--text-primary)"
                                            }}>
                                                {formatCount(client[column.key])}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", bgcolor: "#f8fafc" }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                Total ({visibleClients.length} institutions)
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "var(--color-primary)" }}>
                                {formatCount(visibleTotals.total_users)} users
                            </Typography>
                        </Box>
                    </Box>
                ) : (

                    /* Desktop table */
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    "& th": {
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "var(--text-light)",
                                        textTransform: "uppercase",
                                        letterSpacing: ".04em",
                                        bgcolor: "#f8fafc",
                                        borderBottom: "1px solid var(--border-color)"
                                    }
                                }}>
                                    <TableCell>Institution</TableCell>
                                    <TableCell>Status</TableCell>
                                    {COUNT_COLUMNS.map((column) => (
                                        <TableCell key={column.key} align="right">{column.label}</TableCell>
                                    ))}
                                    <TableCell align="right">Total users</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleClients.map((client) => (
                                    <TableRow
                                        key={client.id}
                                        hover
                                        sx={{ "& td": { borderBottom: "1px solid #f1f5f9" } }}
                                    >
                                        <TableCell sx={{ maxWidth: 320 }}>
                                            <ClientIdentity client={client} />
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={client.status} />
                                        </TableCell>
                                        {COUNT_COLUMNS.map((column) => (
                                            <TableCell key={column.key} align="right" sx={{ fontSize: 14, color: "var(--text-secondary)" }}>
                                                {formatCount(client[column.key])}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ fontSize: 15, fontWeight: 700, color: "var(--color-primary)" }}>
                                            {formatCount(client.total_users)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow sx={{ "& td": { bgcolor: "#f8fafc", fontSize: 14, fontWeight: 700, color: "var(--text-primary)", borderBottom: "none" } }}>
                                    <TableCell colSpan={2}>
                                        Total ({visibleClients.length} institutions)
                                    </TableCell>
                                    {COUNT_COLUMNS.map((column) => (
                                        <TableCell key={column.key} align="right">
                                            {formatCount(visibleTotals[column.key])}
                                        </TableCell>
                                    ))}
                                    <TableCell align="right" sx={{ color: "var(--color-primary) !important" }}>
                                        {formatCount(visibleTotals.total_users)}
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    )
}
